// DirectVideoUpload Component - React Component for Direct Video Upload
import React, { useRef, useState } from 'react';
import { useDirectUpload } from '../../hooks/useDirectUpload';
import { useToast } from '../common/Toast';

const DirectVideoUpload = ({ 
  onUploadComplete, 
  courseId, 
  initialData = {},
  thumbnailFile = null, // Receive thumbnail from parent
  disabled = false 
}) => {
  const fileInputRef = useRef(null);
  const { uploadVideo, cancelUpload, uploading, progress, error, uploadInfo } = useDirectUpload();
  const toastContext = useToast();
  const [selectedFile, setSelectedFile] = useState(null);

  // ✅ Safe toast wrapper
  const toast = {
    success: (msg) => toastContext?.success?.(msg),
    error: (msg) => toastContext?.error?.(msg),
    info: (msg) => toastContext?.info?.(msg),
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      toast.error('الرجاء اختيار ملف فيديو أولاً');
      return;
    }

    try {
      const lessonData = {
        courseId,
        title: initialData.title || 'درس جديد',
        description: initialData.description || undefined,
        contentType: 'video',
        durationSeconds: initialData.durationSeconds || undefined,
        order: initialData.order || 0,
        isFree: initialData.isFree || false,
        freeUntilDate: initialData.freeUntilDate || undefined,
        isLockedForNonSubscribers: initialData.isLockedForNonSubscribers !== undefined 
          ? initialData.isLockedForNonSubscribers 
          : true
      };

      const uploadResult = await uploadVideo(selectedFile, lessonData, thumbnailFile);

      // ✅ تحقق من النجاح وقم بالإجراءات المطلوبة
      if (uploadResult?.success) {
        toast.success('تم رفع الفيديو بنجاح!');

        setSelectedFile(null);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }

        if (onUploadComplete && uploadResult.lesson) {
          onUploadComplete(uploadResult.lesson);
        }
      }

    } catch (err) {
      // Upload error handling

      const message =
        err?.message ||
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        'حدث خطأ غير متوقع أثناء رفع الفيديو';

      toast.error(message);
    }
  };

  const handleCancel = async () => {
    if (uploadInfo?.lessonId) {
      try {
        await cancelUpload(uploadInfo.lessonId);
        toast.info('تم إلغاء الرفع');
        setSelectedFile(null);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      } catch (err) {
        toast.error('فشل إلغاء الرفع');
      }
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div className="direct-video-upload">
      <div className="upload-section">
        <label className="upload-label">
          <span>اختر ملف الفيديو:</span>
          <input
            ref={fileInputRef}
            type="file"
            accept="video/*"
            onChange={handleFileSelect}
            disabled={disabled || uploading}
            className="file-input"
          />
        </label>

        {selectedFile && (
          <div className="file-info">
            <p><strong>اسم الملف:</strong> {selectedFile.name}</p>
            <p><strong>الحجم:</strong> {formatFileSize(selectedFile.size)}</p>
            <p><strong>النوع:</strong> {selectedFile.type}</p>
          </div>
        )}

        {thumbnailFile && (
          <div className="file-info">
            <p><strong>الصورة المصغرة:</strong> {thumbnailFile.name}</p>
            <p><strong>الحجم:</strong> {formatFileSize(thumbnailFile.size)}</p>
            <p><strong>النوع:</strong> {thumbnailFile.type}</p>
          </div>
        )}
      </div>

      {uploading && (
        <div className="upload-progress">
          <div className="progress-bar">
            <div 
              className="progress-fill" 
              style={{ width: `${progress}%` }}
            />
          </div>
          <p>جاري الرفع... {Math.round(progress)}%</p>
          
          <button 
            type="button" 
            onClick={handleCancel}
            className="cancel-btn"
            disabled={!uploadInfo?.lessonId}
          >
            إلغاء الرفع
          </button>
        </div>
      )}

      {error && (
        <div className="upload-error">
          <p className="error-message">{error}</p>
        </div>
      )}

      <div className="upload-actions">
        <button
          type="button"
          onClick={handleUpload}
          disabled={!selectedFile || uploading || disabled}
          className="upload-btn"
        >
          {uploading ? 'جاري الرفع...' : 'رفع الفيديو'}
        </button>
      </div>

      <style data-jsx>{`
        .direct-video-upload {
          background: #f8f9fa;
          border: 1px solid #dee2e6;
          border-radius: 8px;
          padding: 20px;
          margin: 20px 0;
        }

        .upload-section {
          margin-bottom: 20px;
        }

        .upload-label {
          display: block;
          font-weight: 600;
          margin-bottom: 10px;
          color: #495057;
        }

        .file-input {
          width: 100%;
          padding: 10px;
          border: 2px dashed #ced4da;
          border-radius: 4px;
          background: white;
          cursor: pointer;
        }

        .file-input:disabled {
          background: #e9ecef;
          cursor: not-allowed;
        }

        .file-info {
          background: white;
          border: 1px solid #dee2e6;
          border-radius: 4px;
          padding: 15px;
          margin-top: 10px;
        }

        .file-info p {
          margin: 5px 0;
          font-size: 14px;
        }

        .upload-progress {
          margin: 20px 0;
        }

        .progress-bar {
          width: 100%;
          height: 20px;
          background: #e9ecef;
          border-radius: 10px;
          overflow: hidden;
          margin-bottom: 10px;
        }

        .progress-fill {
          height: 100%;
          background: linear-gradient(90deg, #007bff, #0056b3);
          transition: width 0.3s ease;
        }

        .upload-error {
          background: #f8d7da;
          border: 1px solid #f5c6cb;
          color: #721c24;
          padding: 10px;
          border-radius: 4px;
          margin: 10px 0;
        }

        .upload-actions {
          display: flex;
          gap: 10px;
        }

        .upload-btn, .cancel-btn {
          padding: 10px 20px;
          border: none;
          border-radius: 4px;
          cursor: pointer;
          font-weight: 600;
          transition: all 0.2s ease;
        }

        .upload-btn {
          background: #007bff;
          color: white;
        }

        .upload-btn:hover:not(:disabled) {
          background: #0056b3;
        }

        .upload-btn:disabled {
          background: #6c757d;
          cursor: not-allowed;
        }

        .cancel-btn {
          background: #dc3545;
          color: white;
        }

        .cancel-btn:hover:not(:disabled) {
          background: #c82333;
        }

        .cancel-btn:disabled {
          background: #6c757d;
          cursor: not-allowed;
        }
      `}</style>
    </div>
  );
};

export default DirectVideoUpload;